import { css } from '@emotion/css';
import { Link } from 'react-router-dom';
import { appRoutes } from '../../../routing/app-routes';
import {
  AmericanExpressIcon,
  DinersIcon,
  DiscoverIcon,
  IDCheck,
  MaestroIcon,
  MarexLogo,
  MasterCardIcon,
  VisaIcon,
  VisaSecure,
  WSPayIcon,
} from '../../assets/icons';
import { Colors } from '../../styles/colors';
import { labelStyle, p3Style, valueStyle } from '../../styles/text';
import { Layout } from './Layout';
import { BOTTOM_NAV_SAFE_HEIGHT } from '../../styles/css-values';
import { Radius } from '../../styles/tokens';

const cardIssuers = [
  {
    href: 'https://www.mastercard.com/hr/',
    label: 'Mastercard ID Check',
    src: IDCheck,
  },
  {
    href: 'https://www.visa.co.uk/products/visa-secure.html',
    label: 'Visa Secure',
    src: VisaSecure,
  },
  {
    href: 'https://www.mastercard.com/hr/',
    label: 'Maestro',
    src: MaestroIcon,
  },
  {
    href: 'https://www.mastercard.hr/hr-hr.html',
    label: 'Mastercard',
    src: MasterCardIcon,
  },
  { href: 'https://www.visa.com.hr/', label: 'Visa', src: VisaIcon },
  {
    href: 'https://www.wspay.info/',
    label: 'American Express',
    src: AmericanExpressIcon,
  },
];

export const Footer = (): JSX.Element => {
  return (
    <footer className={containerStyle}>
      <Layout.Content className={contentStyle}>
        <nav aria-label="Legal">
          <ul className={listStyle}>
            <li className={listItemStyle}>
              <Link to={appRoutes.privacyPolicy}>Privacy Policy</Link>
            </li>
            <li className={listItemStyle}>
              <Link to={appRoutes.termsOfUse}>Terms of Use</Link>
            </li>
            <li className={listItemStyle}>
              <Link to={appRoutes.termsOfSale}>Terms of Sale</Link>
            </li>
          </ul>
        </nav>

        <div className={paymentsRowStyle}>
          <span className={paymentsLabelStyle}>
            We accept following card issuers:
          </span>
          <div className={cardIconsContainerStyle}>
            {cardIssuers.map((issuer) => (
              <a
                key={issuer.label}
                target="_blank"
                href={issuer.href}
                rel="noreferrer"
                aria-label={issuer.label}
              >
                <img src={issuer.src} alt="" height={20} />
              </a>
            ))}
            <a
              target="_blank"
              href="https://www.diners.com.hr/"
              rel="noreferrer"
              aria-label="Diners Club"
            >
              <DinersIcon />
            </a>
            <a
              target="_blank"
              href="https://www.discover.com/"
              rel="noreferrer"
              aria-label="Discover"
            >
              <DiscoverIcon />
            </a>
          </div>
        </div>

        <div className={wspayRowStyle}>
          <a
            target="_blank"
            href="https://www.wspay.info/"
            rel="noreferrer"
            aria-label="WSPay"
            className={wspayBadgeStyle}
          >
            <WSPayIcon />
          </a>
          <span className={paymentsLabelStyle}>
            We use WSPay for online payments.
          </span>
        </div>

        <div className={bottomRowStyle}>
          <MarexLogo fill={Colors.WINDOW_GRAY} className={logoStyle} />
          <p className={copyrightStyle}>
            © {new Date().getFullYear()} Marex Elektrostroj d.o.o.
          </p>
        </div>
      </Layout.Content>
    </footer>
  );
};

const containerStyle = css`
  width: 100%;
  background-color: ${Colors.SURFACE};
  border-top: 1px solid ${Colors.BORDER};
  margin-top: auto;
  padding-bottom: ${BOTTOM_NAV_SAFE_HEIGHT};
`;

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
`;

const listStyle = css`
  list-style-type: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1.25rem;
`;

const listItemStyle = css`
  ${valueStyle}
  color: ${Colors.CAPRI_BLUE};
  font-weight: bold;
  font-size: 0.9375rem;
  cursor: pointer;

  > a {
    display: inline-block;
    padding: 0.375rem 0;
  }

  @media (hover: hover) {
    &:hover {
      color: ${Colors.CAPRI_BLUE_DARK};
      text-decoration: underline;
      text-underline-offset: 0.2em;
    }
  }
`;

const paymentsRowStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding-top: 1.25rem;
  border-top: 1px solid ${Colors.BORDER};
`;

const paymentsLabelStyle = css`
  ${p3Style}
  color: ${Colors.TEXT_SECONDARY};
`;

const cardIconsContainerStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;

  > a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 1.75rem;
    padding: 0 0.375rem;
    background: ${Colors.WHITE};
    border: 1px solid ${Colors.BORDER};
    border-radius: ${Radius.sm};
  }

  img,
  svg {
    display: block;
    height: 18px;
    width: auto;
  }
`;

const wspayRowStyle = css`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const wspayBadgeStyle = css`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 0.25rem 0.5rem;
  background: ${Colors.WHITE};
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.sm};

  svg {
    display: block;
    height: 26px;
    width: auto;
  }
`;

const bottomRowStyle = css`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid ${Colors.BORDER};
`;

const logoStyle = css`
  flex: 0 0 auto;
  height: 22px;
  width: auto;
`;

const copyrightStyle = css`
  ${labelStyle}
  font-size: 0.8125rem;
  color: ${Colors.TEXT_SECONDARY};
`;
